import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Close as RemoveIcon,
  DeleteOutline as DeleteIcon,
  Save as SaveIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { getFairUrl } from '../../jobBoard/hostSlug';
import AtsFairQr from './AtsFairQr';
import AtsFairPositionPicker from './AtsFairPositionPicker';
import AtsDeleteFairDialog from './AtsDeleteFairDialog';
import { STATE_COLORS, getPublicStateLabel, provisionalState, positionTitle } from './fairHelpers';

const MAX_POSITIONS = 200;
const CARD_SX = { border: '1px solid #E5E7EB', borderRadius: 2, p: 3, mb: 3 };
const PURPLE_SX = { backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } };

/**
 * Crear / editar una feria (PLAN_FERIAS): nombre, vacantes elegidas a mano con
 * su estado en la página pública, y el link + QR (fijos desde que se crea).
 * Rutas: /ats/fairs/new y /ats/fairs/:fairId. Sin permiso de edición (no es
 * admin ni la creó) queda en solo lectura; link y QR siguen disponibles.
 */
const AtsFairEditor = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fairId } = useParams();
  const isNew = !fairId;
  const { organization, authToken, showAlert } = useStore.getState();

  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState(null);
  const [fair, setFair] = useState(null);
  const [boardSlug, setBoardSlug] = useState(null);
  const [name, setName] = useState('');
  const [positions, setPositions] = useState([]);
  const [inaccessibleCount, setInaccessibleCount] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const canEdit = isNew || Boolean(fair?.canEdit);
  const requestConfig = {
    params: { organizationId: organization },
    headers: { Authorization: `Bearer ${authToken}` },
  };

  const applyDetail = (data, board) => {
    setFair(data);
    setName(data?.name || '');
    setPositions(data?.positions || []);
    setInaccessibleCount(data?.inaccessibleCount || 0);
    setBoardSlug(board || null);
    setDirty(false);
  };

  useEffect(() => {
    if (isNew) return;
    const load = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const response = await api.get(`/organizations/job-fairs/${fairId}`, requestConfig);
        const data = response.data.response;
        if (data?.status_response === 'OK') applyDetail(data.data, data.boardSlug);
        else setLoadError(t('ats.fairs.editor.notFound'));
      } catch (err) {
        console.error(err);
        setLoadError(err?.response?.status === 404 ? t('ats.fairs.editor.notFound') : t('ats.fairs.loadError'));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fairId]);

  const handleAdd = (items) => {
    setPositions((prev) => {
      const existing = new Set(prev.map((p) => p.uid));
      const added = items
        .filter((item) => !existing.has(item.uid))
        .map((item) => ({ ...item, publicState: provisionalState(item) }));
      return [...prev, ...added];
    });
    setPickerOpen(false);
    setDirty(true);
  };

  const handleRemove = (uid) => {
    setPositions((prev) => prev.filter((p) => p.uid !== uid));
    setDirty(true);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('ats.fairs.editor.nameRequired'));
      return;
    }
    if (positions.length + inaccessibleCount > MAX_POSITIONS) {
      setError(t('ats.fairs.editor.tooMany', { max: MAX_POSITIONS }));
      return;
    }
    setSaving(true);
    setError(null);
    const body = { name: trimmed, positionIds: positions.map((p) => p.uid) };
    try {
      const response = isNew
        ? await api.post('/organizations/job-fairs', body, requestConfig)
        : await api.put(`/organizations/job-fairs/${fairId}`, body, requestConfig);
      const data = response.data.response;
      if (data?.status_response === 'OK') {
        applyDetail(data.data, data.boardSlug);
        if (isNew) {
          showAlert(t('ats.fairs.editor.created'), 'success');
          navigate(`/ats/fairs/${data.data.uid}`, { replace: true });
        } else {
          showAlert(t('ats.fairs.editor.saved'), 'success');
        }
      } else {
        setError(t('ats.fairs.editor.saveError'));
      }
    } catch (err) {
      console.error(err);
      const errorType = err?.response?.data?.response?.errorType;
      if (errorType === 'POSITION_NOT_ALLOWED') setError(t('ats.fairs.editor.notAllowed'));
      else if (errorType === 'FAIR_EDIT_FORBIDDEN') setError(t('ats.fairs.editor.forbidden'));
      else if (errorType === 'NAME_REQUIRED') setError(t('ats.fairs.editor.nameRequired'));
      else if (errorType === 'TOO_MANY_POSITIONS') setError(t('ats.fairs.editor.tooMany', { max: MAX_POSITIONS }));
      else setError(t('ats.fairs.editor.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const backButton = (
    <Button
      startIcon={<BackIcon />}
      onClick={() => navigate('/ats/fairs')}
      sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}
    >
      {t('ats.fairs.editor.back')}
    </Button>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        {backButton}
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  const fairUrl = fair?.slug ? getFairUrl(fair.slug, boardSlug) : null;

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      {backButton}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" fontWeight={700}>
            {isNew ? t('ats.fairs.editor.newTitle') : (fair?.name || t('ats.fairs.editor.editTitle'))}
          </Typography>
          {fair?.createdByName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('ats.fairs.editor.createdBy', { name: fair.createdByName })}
            </Typography>
          )}
        </Box>
        {canEdit && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {dirty && !isNew && (
              <Typography variant="caption" color="warning.main" fontWeight={600}>
                {t('ats.fairs.editor.unsaved')}
              </Typography>
            )}
            {!isNew && (
              <Button color="error" variant="outlined" startIcon={<DeleteIcon />} onClick={() => setDeleteOpen(true)} disabled={saving}>
                {t('ats.fairs.delete')}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={saving || (!isNew && !dirty)}
              onClick={handleSave}
              sx={PURPLE_SX}
            >
              {isNew ? t('ats.fairs.editor.create') : t('ats.fairs.editor.save')}
            </Button>
          </Box>
        )}
      </Box>

      {!canEdit && (
        <Alert severity="info" icon={<LockIcon />} sx={{ mb: 3 }}>
          {t('ats.fairs.editor.readOnly')}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Paper elevation={0} sx={CARD_SX}>
        <TextField
          label={t('ats.fairs.editor.name')}
          placeholder={t('ats.fairs.editor.namePlaceholder')}
          value={name}
          onChange={(e) => { setName(e.target.value); setDirty(true); }}
          fullWidth
          disabled={!canEdit || saving}
          inputProps={{ maxLength: 120 }}
        />
      </Paper>

      <Paper elevation={0} sx={CARD_SX}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('ats.fairs.editor.positionsTitle')} ({positions.length + inaccessibleCount})
          </Typography>
          {canEdit && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setPickerOpen(true)} disabled={saving}>
              {t('ats.fairs.editor.addPositions')}
            </Button>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('ats.fairs.editor.positionsHint')}
        </Typography>

        {positions.length === 0 && inaccessibleCount === 0 && (
          <Alert severity="info">{t('ats.fairs.editor.noPositions')}</Alert>
        )}

        {positions.map((item) => (
          <Box
            key={item.uid}
            data-testid={`fair-position-${item.uid}`}
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderBottom: '1px solid #F3F4F6' }}
          >
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {item.missing ? t('ats.fairs.editor.missingPosition') : positionTitle(item)}
              </Typography>
              {!item.missing && (
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {[item.projectName, item.ubicacion, item.tipo ? t(`ats.positions.wizard.options.tipo.${item.tipo}`) : null].filter(Boolean).join(' · ')}
                </Typography>
              )}
            </Box>
            <Chip
              size="small"
              color={STATE_COLORS[item.publicState] || 'default'}
              variant={item.publicState === 'shown' ? 'filled' : 'outlined'}
              label={getPublicStateLabel(item, t)}
            />
            {canEdit && (
              <Tooltip title={t('ats.fairs.editor.remove')}>
                <IconButton size="small" onClick={() => handleRemove(item.uid)} disabled={saving} aria-label={t('ats.fairs.editor.remove')}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ))}

        {inaccessibleCount > 0 && (
          <Alert severity="info" icon={<LockIcon />} sx={{ mt: 2 }}>
            {t('ats.fairs.editor.inaccessible', { count: inaccessibleCount })}
          </Alert>
        )}
      </Paper>

      <Paper elevation={0} sx={CARD_SX}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          {t('ats.fairs.editor.linkTitle')}
        </Typography>
        {fairUrl ? (
          <AtsFairQr url={fairUrl} slug={fair.slug} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('ats.fairs.editor.linkAfterSave')}
          </Typography>
        )}
        {/* La feria no depende de "Publicar la web de empleos" (Erwin,
            2026-10-05): sin web publicada el link va a app.amandahr.ai */}
        {fairUrl && !boardSlug && (
          <Alert severity="info" sx={{ mt: 2 }} data-testid="fair-board-unpublished">
            {t('ats.fairs.editor.boardNotPublished')}
          </Alert>
        )}
      </Paper>

      {canEdit && (
        <AtsFairPositionPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          excludeIds={positions.map((p) => p.uid)}
          onAdd={handleAdd}
        />
      )}
      {!isNew && canEdit && (
        <AtsDeleteFairDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          fairId={fairId}
          fairName={fair?.name}
          onDeleted={() => navigate('/ats/fairs')}
        />
      )}
    </Box>
  );
};

export default AtsFairEditor;
